import {
  AppShellContent,
  Button,
  Group,
  Stack,
  Text,
  Title,
  toast,
} from "@assetwatch/ui";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  MotionAmplificationJob,
  MotionAmplificationJobId,
  MotionAmplificationParameters,
  MotionAmplificationPreset,
} from "../clients/motion-amplification";
import { MotionAmplificationDetailsModal } from "../components/motion-amplification/MotionAmplificationDetailsModal";
import { MotionAmplificationJobFilters } from "../components/motion-amplification/MotionAmplificationJobFilters";
import { MotionAmplificationRetryPrompt } from "../components/motion-amplification/MotionAmplificationRetryPrompt";
import { MotionAmplificationUploadModal } from "../components/motion-amplification/MotionAmplificationUploadModal";
import {
  defaultMotionAmplificationFilters,
  matchesMotionAmplificationFilters,
} from "../components/motion-amplification/motionAmplificationFilters";
import type { JobView } from "../components/motion-amplification/types";
import { MotionAmplificationGalleryAdapter } from "../components/snowflakes/MotionAmplificationGalleryAdapter";
import { getMultipartFileFromHandle } from "../mutations/multipartUploadFileHandleStore";
import {
  fingerprintFile,
  type MultipartUploadSession,
} from "../mutations/multipartUploadSession";
import {
  expirePausedMultipartUpload,
  showPausedMultipartUploadToast,
} from "../mutations/pausedMultipartUploadToast";
import { useRetryMotionAmplificationJobMutation } from "../mutations/useRetryMotionAmplificationJobMutation";
import { useStartMotionAmplificationJobMutation } from "../mutations/useStartMotionAmplificationJobMutation";
import {
  type ActiveMotionAmplificationUploads,
  type MotionAmplificationSubmission,
  useUploadMotionAmplificationVideoMutation,
} from "../mutations/useUploadMotionAmplificationVideoMutation";
import { useMotionAmplificationRawVideosQuery } from "../queries/useMotionAmplificationRawVideosQuery";

type MotionAmplificationJobsQueryState = {
  data: MotionAmplificationJob[] | undefined;
  error: Error | null;
  isLoading: boolean;
  refetch?: () => Promise<unknown>;
  uploadSessionStatusByJobId?: Map<
    MotionAmplificationJobId,
    {
      sessionId: string;
      status: string;
      canResume?: boolean;
      fileFingerprint?: string;
      fileSizeBytes?: number;
      partSizeBytes?: number;
    }
  >;
};

type PendingResume = {
  session: MultipartUploadSession;
  job: MotionAmplificationJob;
};

type FailedJobDecision = {
  jobId: MotionAmplificationJobId;
  jobName: string;
};

function defaultUploadState(
  job: MotionAmplificationJob,
): JobView["uploadState"] {
  if (job.status === "Completed") return "completed";
  if (job.status === "Failed") return "failed";
  return "processing";
}

/**
 * The server has no concept of "uploading"/"starting" - those only exist
 * while the client is PUTting the file to S3 and waiting for the job to
 * start. `activeUploads` overlays that client-only progress onto the jobs
 * it belongs to; every other job's view is derived purely from its server
 * status.
 */
function toJobView(
  job: MotionAmplificationJob,
  activeUploads: ActiveMotionAmplificationUploads,
): JobView {
  const activeProgress = activeUploads.get(job.jobId);
  if (activeProgress !== undefined) {
    return {
      job,
      uploadProgress: activeProgress,
      uploadState: activeProgress < 100 ? "uploading" : "starting",
    };
  }
  return {
    job,
    uploadProgress:
      job.status === "Completed" ? 100 : (job.progressPercent ?? 0),
    uploadState: defaultUploadState(job),
  };
}

export function MotionAmplificationScreen({
  jobsQuery,
}: {
  jobsQuery: MotionAmplificationJobsQueryState;
}) {
  const { t } = useTranslation();
  const rawVideosQuery = useMotionAmplificationRawVideosQuery();
  const { uploadVideo, activeUploads } =
    useUploadMotionAmplificationVideoMutation();
  const retryJobMutation = useRetryMotionAmplificationJobMutation();
  const { mutate: startJob } = useStartMotionAmplificationJobMutation();
  const knownJobStatuses = useRef<
    Map<MotionAmplificationJobId, MotionAmplificationJob["status"]>
  >(new Map());
  const [pendingResume, setPendingResume] = useState<PendingResume | null>(
    null,
  );
  const notifiedInterruptedJobs = useRef(new Set<MotionAmplificationJobId>());
  const automaticallyStartedJobs = useRef(new Set<MotionAmplificationJobId>());
  const [failedJobsAwaitingDecision, setFailedJobsAwaitingDecision] = useState<
    FailedJobDecision[]
  >([]);
  const jobViews = (jobsQuery.data ?? [])
    .map((job) => toJobView(job, activeUploads))
    .sort((a, b) =>
      (b.job.dateCreated || "").localeCompare(a.job.dateCreated || ""),
    );
  const [filters, setFilters] = useState(defaultMotionAmplificationFilters);
  const visibleJobViews = jobViews.filter(({ job }) => job.status !== "Failed");
  const filteredJobViews = visibleJobViews.filter(({ job }) =>
    matchesMotionAmplificationFilters(job, filters),
  );
  const [selectedJobId, setSelectedJobId] =
    useState<MotionAmplificationJobId | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detailsJobId, setDetailsJobId] =
    useState<MotionAmplificationJobId | null>(null);
  const detailsJob =
    jobViews.find(({ job }) => job.jobId === detailsJobId)?.job ?? null;
  const hasNoMatchingJobs =
    visibleJobViews.length > 0 && filteredJobViews.length === 0;

  useEffect(() => {
    const failedJobs: FailedJobDecision[] = [];

    for (const job of jobsQuery.data ?? []) {
      const previousStatus = knownJobStatuses.current.get(job.jobId);
      knownJobStatuses.current.set(job.jobId, job.status);
      if (
        previousStatus &&
        previousStatus !== "Failed" &&
        job.status === "Failed"
      ) {
        failedJobs.push({
          jobId: job.jobId,
          jobName: job.name ?? job.fileName ?? "",
        });
      }
    }

    if (failedJobs.length === 0) return;
    setFailedJobsAwaitingDecision((current) => [
      ...current,
      ...failedJobs.filter(
        ({ jobId }) => !current.some((job) => job.jobId === jobId),
      ),
    ]);
  }, [jobsQuery.data]);

  const failedJobAwaitingDecision = failedJobsAwaitingDecision[0] ?? null;

  function resolveFailedJobDecision(jobId: MotionAmplificationJobId) {
    setFailedJobsAwaitingDecision((current) =>
      current.filter((job) => job.jobId !== jobId),
    );
    void jobsQuery.refetch?.();
  }

  async function retryFailedJob(jobId: MotionAmplificationJobId) {
    await retryJobMutation.mutateAsync(jobId);
    resolveFailedJobDecision(jobId);
  }

  // Resolving at hand-off, not completion, frees the modal for another upload.
  function uploadUntilStarted(
    submission: MotionAmplificationSubmission,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      let handedOff = false;
      function handOff(jobId: MotionAmplificationJobId) {
        if (handedOff) return;
        handedOff = true;
        setPendingResume(null);
        setSelectedJobId(jobId);
        setUploadOpen(false);
        resolve();
      }
      uploadVideo({ ...submission, onUploadStarted: handOff }).then(
        handOff,
        reject,
      );
    });
  }

  async function handleUpload(submission: MotionAmplificationSubmission) {
    let nextSubmission = submission;
    if (pendingResume && !submission.file) {
      toast.error(t("motionAmplification.resumeFileMismatch"));
      throw new Error("A file must be selected to resume this upload.");
    }
    if (pendingResume && submission.file) {
      if (
        submission.file.size !== pendingResume.session.fileSize ||
        (await fingerprintFile(submission.file)) !==
          pendingResume.session.fingerprint
      ) {
        toast.error(t("motionAmplification.resumeFileMismatch"));
        // Keep the upload modal open so the user can choose the correct file.
        throw new Error(
          "The selected file does not match the interrupted upload.",
        );
      }
      nextSubmission = { ...submission, resumeSession: pendingResume.session };
    }
    await uploadUntilStarted(nextSubmission);
  }

  async function handleResumeFileSelected(
    file: File,
    fileHandle?: FileSystemFileHandle,
  ) {
    const resume = pendingResume;
    if (!resume) return;
    if (
      file.size !== resume.session.fileSize ||
      (await fingerprintFile(file)) !== resume.session.fingerprint
    ) {
      toast.error(t("motionAmplification.resumeFileMismatch"));
      throw new Error(t("motionAmplification.resumeFileMismatch"));
    }

    const job = resume.job;
    await uploadUntilStarted({
      file,
      fileHandle,
      resumeSession: resume.session,
      existingVideo: null,
      name: job.name ?? job.fileName ?? file.name,
      description: job.description ?? "",
      preset: (job.preset ?? "standard-60fps") as MotionAmplificationPreset,
      parameters: job.parameters as MotionAmplificationParameters,
      fps: job.parameters?.fps ?? null,
      width: null,
      height: null,
      machineId: job.assignment?.machineId ?? null,
    });
  }

  async function handleResumeUpload(jobId: MotionAmplificationJobId) {
    const job = jobsQuery.data?.find((item) => item.jobId === jobId);
    const status = jobsQuery.uploadSessionStatusByJobId?.get(jobId);
    if (
      !job ||
      !status?.canResume ||
      !status.fileFingerprint ||
      !status.fileSizeBytes ||
      !status.partSizeBytes
    )
      return;
    const session: MultipartUploadSession = {
      jobId,
      sessionId: status.sessionId,
      partSize: status.partSizeBytes,
      fileSize: status.fileSizeBytes,
      fingerprint: status.fileFingerprint,
    };
    const file = await getMultipartFileFromHandle(jobId).catch(() => null);
    if (!file) {
      setPendingResume({ session, job });
      setUploadOpen(true);
      return;
    }
    if (
      file.size !== session.fileSize ||
      (await fingerprintFile(file)) !== session.fingerprint
    ) {
      toast.error(t("motionAmplification.resumeFileMismatch"));
      setPendingResume({ session, job });
      setUploadOpen(true);
      return;
    }
    try {
      await uploadVideo({
        file,
        resumeSession: session,
        existingVideo: null,
        name: job.name ?? job.fileName ?? file.name,
        description: job.description ?? "",
        preset: (job.preset ?? "standard-60fps") as MotionAmplificationPreset,
        parameters: job.parameters as MotionAmplificationParameters,
        fps: job.parameters?.fps ?? null,
        width: null,
        height: null,
        machineId: job.assignment?.machineId ?? null,
      });
      setSelectedJobId(jobId);
    } catch {
      // Upload errors are reported through the mutation's toast handling.
    }
  }

  const resumeUploadHandler = useRef(handleResumeUpload);
  resumeUploadHandler.current = handleResumeUpload;

  function showPausedUpload(job: MotionAmplificationJob, sessionId: string) {
    showPausedMultipartUploadToast({
      jobId: job.jobId,
      jobName: job.name ?? job.fileName ?? job.jobId,
      onResume: () => void resumeUploadHandler.current(job.jobId),
      onExpire: () =>
        expirePausedMultipartUpload(
          { jobId: job.jobId, sessionId },
          () => jobsQuery.refetch?.() ?? Promise.resolve(),
        ),
    });
  }

  function handleUploadModalOpenChange(open: boolean) {
    setUploadOpen(open);
    if (!open && pendingResume) {
      showPausedUpload(pendingResume.job, pendingResume.session.sessionId);
    }
  }

  useEffect(() => {
    for (const job of jobsQuery.data ?? []) {
      const session = jobsQuery.uploadSessionStatusByJobId?.get(job.jobId);
      if (
        job.status !== "UploadPending" ||
        !session?.canResume ||
        activeUploads.has(job.jobId) ||
        notifiedInterruptedJobs.current.has(job.jobId)
      ) {
        continue;
      }

      notifiedInterruptedJobs.current.add(job.jobId);
      showPausedMultipartUploadToast({
        jobId: job.jobId,
        jobName: job.name ?? job.fileName ?? job.jobId,
        onResume: () => void resumeUploadHandler.current(job.jobId),
        onExpire: () =>
          expirePausedMultipartUpload(
            { jobId: job.jobId, sessionId: session.sessionId },
            () => jobsQuery.refetch?.() ?? Promise.resolve(),
          ),
      });
    }
  }, [
    activeUploads,
    jobsQuery.data,
    jobsQuery.refetch,
    jobsQuery.uploadSessionStatusByJobId,
  ]);

  useEffect(() => {
    for (const job of jobsQuery.data ?? []) {
      const session = jobsQuery.uploadSessionStatusByJobId?.get(job.jobId);
      if (job.status !== "UploadPending" || session?.status !== "completed")
        continue;
      if (
        activeUploads.has(job.jobId) ||
        automaticallyStartedJobs.current.has(job.jobId)
      )
        continue;
      automaticallyStartedJobs.current.add(job.jobId);
      startJob(job.jobId);
    }
  }, [
    activeUploads,
    jobsQuery.data,
    jobsQuery.uploadSessionStatusByJobId,
    startJob,
  ]);

  return (
    <AppShellContent
      pageHeader={
        <Group justify="between" align="center">
          <Title size="lg">{t("motionAmplification.pageTitle")}</Title>
          <Button icon="add-plus" onClick={() => setUploadOpen(true)}>
            {t("motionAmplification.addVideo")}
          </Button>
        </Group>
      }
    >
      <Stack gap={24}>
        {jobsQuery.error && (
          <Text tone="negative">
            {jobsQuery.error.message || t("motionAmplification.loadJobsError")}
          </Text>
        )}
        <MotionAmplificationJobFilters
          filters={filters}
          jobs={jobViews.map(({ job }) => job)}
          onFiltersChange={setFilters}
        />
        {hasNoMatchingJobs ? (
          <Text tone="subtle">{t("motionAmplification.noSearchResults")}</Text>
        ) : (
          <MotionAmplificationGalleryAdapter
            jobs={filteredJobViews}
            isLoading={jobsQuery.isLoading}
            selectedJobId={selectedJobId}
            onSelectJob={setSelectedJobId}
            onShowDetails={(job) => setDetailsJobId(job.jobId)}
            onAddVideo={() => setUploadOpen(true)}
          />
        )}
        <MotionAmplificationUploadModal
          open={uploadOpen}
          onOpenChange={handleUploadModalOpenChange}
          onSubmit={handleUpload}
          rawVideos={rawVideosQuery.data ?? []}
          isLoadingRawVideos={rawVideosQuery.isLoading}
          resumeUpload={
            pendingResume
              ? {
                  jobName:
                    pendingResume.job.name ??
                    pendingResume.job.fileName ??
                    pendingResume.job.jobId,
                  fileName:
                    pendingResume.job.fileName ??
                    pendingResume.job.name ??
                    pendingResume.job.jobId,
                  fileSize: pendingResume.session.fileSize,
                }
              : undefined
          }
          onResumeFileSelected={handleResumeFileSelected}
        />
        <MotionAmplificationDetailsModal
          job={detailsJob}
          open={detailsJobId !== null}
          onOpenChange={(open) => {
            if (!open) setDetailsJobId(null);
          }}
        />
        <MotionAmplificationRetryPrompt
          open={failedJobAwaitingDecision !== null}
          jobName={failedJobAwaitingDecision?.jobName ?? ""}
          isRetrying={retryJobMutation.isPending}
          onRetry={() => {
            if (failedJobAwaitingDecision) {
              void retryFailedJob(failedJobAwaitingDecision.jobId).catch(
                () => undefined,
              );
            }
          }}
          onDecline={() => {
            if (failedJobAwaitingDecision) {
              resolveFailedJobDecision(failedJobAwaitingDecision.jobId);
            }
          }}
        />
      </Stack>
    </AppShellContent>
  );
}
